import { GeoCoordinate, GeoSearch, MILES_TO_METERS } from '@aloft/toolkit-shared';
import { Flex, Typography } from '@aloft/toolkit-sui';

import type { ReactNode } from 'react';

import {
  LocationType,
  formatProximityRequirementPolygonTooSmall,
  formatProximityRequirementRadiusTooSmall,
} from '../../../../utils/locationTypeProximityRequirementMiles.ts';
import { proximityMiles } from '../../../../utils/proximityMiles.ts';
import { MapAlert } from './MapAlert.tsx';

type MapAlertsProps = {
  geoSearch: GeoSearch;
  locationType: LocationType | undefined;
  proximityRequirementMiles: number | undefined;
  subjectLat: number;
  subjectLng: number;
};

function polygonExceedsProximityRequirement({
  coords,
  subjectLat,
  subjectLng,
  proximityRequirementMiles,
}: {
  coords: GeoCoordinate[];
  subjectLat: number;
  subjectLng: number;
  proximityRequirementMiles: number;
}): boolean {
  return coords.some(([lng, lat]) => {
    const miles = proximityMiles(
      { latitude: subjectLat, longitude: subjectLng },
      { latitude: lat, longitude: lng },
    );
    return miles !== undefined && miles > proximityRequirementMiles;
  });
}

export const MapAlerts = ({
  geoSearch,
  locationType,
  proximityRequirementMiles,
  subjectLat,
  subjectLng,
}: MapAlertsProps) => {
  const alerts: ReactNode[] = [];

  if (geoSearch.t === 'polygon') {
    if (!geoSearch.coords.length) {
      alerts.push(
        <Typography variant="body1" key="define-boundary" color="textOffset">
          Define a geographical boundary by drawing a polygon (‘Esc’ to cancel).
        </Typography>,
      );
    } else {
      if (
        locationType !== undefined &&
        proximityRequirementMiles !== undefined &&
        polygonExceedsProximityRequirement({
          coords: geoSearch.coords,
          subjectLat,
          subjectLng,
          proximityRequirementMiles,
        })
      ) {
        const msg = formatProximityRequirementPolygonTooSmall(
          locationType,
          proximityRequirementMiles,
        );
        alerts.push(
          <Typography variant="body1" key="proximity-requirement" color="textOffset">
            {msg}
          </Typography>,
        );
      }
      alerts.push(
        <Typography variant="body1" key="apply-polygon" color="textOffset">
          Click on &quot;Apply&quot; to search comps with the current polygon.
        </Typography>,
      );
    }
  } else {
    if (
      locationType !== undefined &&
      proximityRequirementMiles !== undefined &&
      geoSearch.radiusInMeters > proximityRequirementMiles * MILES_TO_METERS
    ) {
      const msg = formatProximityRequirementRadiusTooSmall(locationType, proximityRequirementMiles);
      alerts.push(
        <Typography variant="body1" key="proximity-requirement" color="textOffset">
          {msg}
        </Typography>,
      );
    }
    alerts.push(
      <Typography variant="body1" key="apply-radius" color="textOffset">
        Click on &quot;Apply&quot; to search comps with the current radius.
      </Typography>,
    );
  }

  return (
    <Flex position="absolute" bottom="110px" width="100%" justifyContent="center">
      <MapAlert>
        <Flex
          flexDirection="column"
          gap="8px"
          alignItems="center"
          __dangerDoNotUseSx={{ textAlign: 'center' }}
        >
          {alerts}
        </Flex>
      </MapAlert>
    </Flex>
  );
};
